import React, { useState, useReducer, useMemo } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import AbcData from './fragments/AbcData'
import Button from 'components/Button'
import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Select, Checkbox } from 'components/forms'
import { CashRegisters, TimePeriodFilter, filterReducer } from '../filterFragments'
import { initAbcStatistics, getStatisticsData } from '../../gql/getAbcStatistics'
import { listProducts } from 'sections/product/gql/query'
import Divider from 'components/Divider'
import { useStatisticsData } from '../helpers'
import { CardsRow, CardCol } from '../filterFragments/styled'
import FilterCardRemote from 'components/FilterCard/FilterCardRemote'
import isEmpty from 'lodash/isEmpty'
import mergeAbcComparison from 'helpers/mergeAbcComparison.mjs'
import { listCashRegisters } from 'gql/query'
import { useQuery } from 'components/graphql'
import ProfileSettings from 'components/ProfileSettings/ProfileSettings'

const COMPARE_MATCH_KEYS = {
	product: ['productId'],
	shopProduct: ['productId', 'shopName'],
	productShop: ['productId', 'shopName'],
	dateProduct: ['productId', 'dateTime'],
	productDate: ['productId', 'dateTime'],
	productDateShop: ['productId', 'shopName', 'dateTime']
}

const GROUP_BY_OPTIONS = {
	product: {
		groupByColumns: [],
		filterColumns: ['shopName', 'dateTime']
	},
	shopProduct: {
		groupByColumns: ['shopName'],
		filterColumns: ['dateTime']
	},
	productShop: {
		groupByColumns: ['name'],
		filterColumns: ['dateTime']
	},
	dateProduct: {
		groupByColumns: ['dateTime'],
		filterColumns: ['shopName']
	},
	productDate: {
		groupByColumns: ['name'],
		filterColumns: ['shopName']
	},
	productDateShop: {
		groupByColumns: ['name', 'dateTime'],
		filterColumns: []
	}
}

const AbcStatisticsFilter = () => {
	const [expandedId, setExpandedId] = useState(null)
	const [compareDatePeriod, setCompareDatePeriod] = useState(null)
	const [visibleCompare, setVisibleCompare] = useState(false)
	const [visibleRotation, setVisibleRotation] = useState(false)
	const [t] = useTranslation()
	const { data: crData } = useQuery(listCashRegisters, { fetchPolicy: 'cache-first' })

	const defaultFilter = {
		from: moment().utc().startOf('day').toISOString(),
		to: moment().utc().endOf('day').toISOString(),
		cashRegisterIds: crData?.listCashRegisters?.map(({ id }) => id) || [],
		productIds: [],
		groupBy: 'product',
		unitedAgainstWaste: false,
		goodsFlow: true,
		enableComparison: false
	}
	const [filterPrepare, dispatchFilter] = useReducer(filterReducer, defaultFilter)

	const { getStatistics, status, data, loading, called } = useStatisticsData({
		getQuery: getStatisticsData,
		initQuery: initAbcStatistics
	})
	const { getStatistics: getStatisticsCompare, data: compareData, loading: compareLoading } = useStatisticsData({
		getQuery: getStatisticsData,
		initQuery: initAbcStatistics
	})
	const finishData = useMemo(() => {
		const matchKeys = COMPARE_MATCH_KEYS[filterPrepare.groupBy] || ['productId']
		return mergeAbcComparison(data, compareData, matchKeys)
	}, [data, compareData, filterPrepare.groupBy])

	const { from, to, cashRegisterIds, productIds, groupBy, unitedAgainstWaste, goodsFlow, enableComparison } = filterPrepare
	const { groupByColumns, filterColumns } = GROUP_BY_OPTIONS[filterPrepare.groupBy]

	const onChangeCompareDatePeriod = value => {
		setCompareDatePeriod(value)
		dispatchFilter({ type: 'enableComparison', value: !isEmpty(value) })
	}
	const handleSetFilter = () => {
		const { enableComparison: comparisonIsActive, ...params } = filterPrepare
		getStatistics({ variables: { params } })
		if(comparisonIsActive) {
			const compareParams = { ...params, from: compareDatePeriod.from, to: compareDatePeriod.to }
			getStatisticsCompare({ variables: { params: compareParams } })
		}
		setVisibleCompare(comparisonIsActive)
		setVisibleRotation(params.goodsFlow)
		setExpandedId('1')
	}

	const handleSetProfileSettings = settings => {
		const filterToSet = settings ? settings : defaultFilter
		dispatchFilter({
			type: 'hydrateFromSaved',
			value: filterToSet
		})
	}

	return(
		<ExpandableBlocks expandedId={expandedId} onExpand={setExpandedId}>
			<ContentBlock blockId='0' startExpanded={!called} label={t('common.strings.settings')}>
				<Row>
					<Col xs={12} style={{ marginLeft: -20 }}>
						<ProfileSettings filters={filterPrepare} onUpdateFilters={handleSetProfileSettings} nameLocalStorage={'abcStatisticsProfiles'} />
					</Col>
				</Row>
				<Row>
					<Col xs>
						<Select
							noForm
							value={groupBy}
							label={t('statistic_section.strings.grouped_by')}
							onChange={({ value }) => dispatchFilter({ type: 'groupBy', value })}
							options={[
								{ label: t('common.strings.product'), value: 'product' },
								{ label: `${t('common.strings.branch')}-${t('common.strings.product')}`, value: 'shopProduct' },
								{ label: `${t('common.strings.product')}-${t('common.strings.branch')}`, value: 'productShop' },
								{ label: `${t('common.fields.date')}-${t('common.strings.product')}`, value: 'dateProduct' },
								{ label: `${t('common.strings.product')}-${t('common.fields.date')}`, value: 'productDate' },
								{ label: `${t('common.strings.product')}-${t('common.fields.date')}-${t('common.strings.branch')}`, value: 'productDateShop' }
							]}
						/>
						<Checkbox
							noForm
							onChange={e => dispatchFilter({ type: 'goodsFlow', value: e.target.checked })}
							checked={goodsFlow}
							label={t('statistic_section.fields.goods_flow')}
						/>
						<Checkbox
							noForm
							onChange={e => dispatchFilter({ type: 'unitedAgainstWaste', value: e.target.checked })}
							checked={unitedAgainstWaste}
							label={t('statistic_section.fields.united_against_waste')}
						/>
						<Checkbox
							disabled={!compareDatePeriod}
							noForm
							onChange={e => dispatchFilter({ type: 'enableComparison', value: e.target.checked })}
							checked={enableComparison}
							label={t('statistic_section.strings.show_comparison_columns')}
						/>
					</Col>
				</Row>
				<Divider />
				<div>
					<ExpandableBlocks nested>
						<ContentBlock startExpanded label={t('common.buttons.filter')}>
							<CardsRow>
								<CardCol>
									<TimePeriodFilter
										value={{ to, from }}
										onChange={value => dispatchFilter({ value, type: 'datePeriod' })}
									/>
								</CardCol>
								<CardCol>
									<CashRegisters
										value={cashRegisterIds}
										onChange={value => dispatchFilter({ value, type: 'cashRegisterIds' })}
									/>
								</CardCol>
								<CardCol>
									<FilterCardRemote
										listQuery={listProducts}
										label={t('common.strings.product')}
										allItemsLabel={t('common.strings.all_products')}
										value={productIds}
										onChange={value => dispatchFilter({ value, type: 'productIds' })}
										withSearch={true}
									/>
								</CardCol>
								<CardCol>
									<TimePeriodFilter
										emptyLabel={t('statistic_section.strings.not_selected')}
										label={t('statistic_section.strings.compare_with')}
										value={{ from: compareDatePeriod?.from, to: compareDatePeriod?.to }}
										onChange={onChangeCompareDatePeriod}
										onRemoveItem={() => onChangeCompareDatePeriod(null)}
										startValue
									/>
								</CardCol>
							</CardsRow>
						</ContentBlock>
					</ExpandableBlocks>
				</div>
				<Divider />
				<Col type='row'>
					<Button text={t('common.buttons.calculate')} onClick={handleSetFilter}/>
				</Col>
			</ContentBlock>
			{called &&
				<AbcData
					key={filterPrepare?.groupBy}
					loading={loading && compareLoading}
					tableLoading={loading || compareLoading}
					data={finishData || data}
					compareData={compareData}
					groupByColumns={groupByColumns}
					filterColumns={filterColumns}
					visibleCompare={visibleCompare}
					visibleRotation={visibleRotation}
					{...status === 'ERROR' && { noDataText: t('server_errors.request_failed') }}
				/>
			}
		</ExpandableBlocks>
	)
}

export default AbcStatisticsFilter
